import {
  useContext,
  createContext,
  useState,
  ReactNode,
  useEffect,
} from 'react'
import { Company, CompanyTheme } from '../types'
import * as Sentry from '@sentry/nextjs'

interface IThemeContextProps {
  theme: CompanyTheme | undefined
  company: Company | undefined
}

export const ThemeContext = createContext<IThemeContextProps>(
  {} as IThemeContextProps
)

export const hexToHSL = (hex: string): string => {
  hex = hex.replace(/^#/, '')
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('')
  }
  if (hex.length !== 6) {
    alert('Invalid HEX color. please follow the format: #123456')

    return '0 0% 0%'
  }

  // Convert hex to RGB
  const bigint = parseInt(hex, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255

  // Convert RGB to HSL
  const rNormalized = r / 255
  const gNormalized = g / 255
  const bNormalized = b / 255

  const max = Math.max(rNormalized, gNormalized, bNormalized)
  const min = Math.min(rNormalized, gNormalized, bNormalized)
  const delta = max - min

  let h = 0
  let s = 0
  let l = (max + min) / 2

  if (delta !== 0) {
    s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min)

    switch (max) {
      case rNormalized:
        h =
          ((gNormalized - bNormalized) / delta +
            (gNormalized < bNormalized ? 6 : 0)) %
          6
        break
      case gNormalized:
        h = (bNormalized - rNormalized) / delta + 2
        break
      case bNormalized:
        h = (rNormalized - gNormalized) / delta + 4
        break
    }
    h *= 60
  }

  h = Math.round(h)
  s = Math.round(s * 100)
  l = Math.round(l * 100)

  return `${h} ${s}% ${l}%`
}

export const ThemeContextProvider = ({
  children,
  company,
}: {
  children: ReactNode
  company: Company
}) => {
  const [theme, setTheme] = useState<CompanyTheme>({
    textColor: '#ffffff',
    primaryColor: '#004225',
    secondaryColor: '#004225',
    logo: '',
  })

  useEffect(() => {
    if (!company) {
      return
    }

    setTheme({
      textColor: company.theme?.textColor ?? '#ffffff',
      secondaryColor: company.theme?.secondaryColor ?? '#E5ECE8',
      primaryColor: company.theme?.primaryColor ?? '#000000',
      logo: company.logo ?? '/logo.webp',
      googleAnalyticsId: company.theme?.googleAnalyticsId,
      googleAnalyticsCrossDomainUrl:
        company.theme?.googleAnalyticsCrossDomainUrl,
    })

    Sentry.setTag('company_id', company.id)
  }, [company])

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--primary',
      hexToHSL(theme.primaryColor)
    )

    document.documentElement.style.setProperty(
      '--accent',
      hexToHSL(theme.secondaryColor)
    )

    document.documentElement.style.setProperty(
      '--primary-foreground',
      hexToHSL(theme.textColor)
    )
  }, [theme])

  return (
    <ThemeContext.Provider
      value={{
        theme,
        company,
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

const useTheme = () => useContext(ThemeContext)

export default useTheme
